import React from 'react';
import { X, FileText, Calendar, CreditCard, Hash, MessageSquare, CheckCircle2, Clock, XCircle, User, Building2 } from 'lucide-react';
import Button from '../../common/Button.js';
import { useTranslation } from 'react-i18next';


/**
 * Path: src/components/company-subscription/subscription_payment/ViewPaymentModal.js
 *
 * Props:
 *   payment   – payment object (from to_dict)
 *   onClose   – fn
 */
const ViewPaymentModal = ({ payment, onClose }) => {
  const { t } = useTranslation();
  if (!payment) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const statusConfig = {
    completed: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 },
    pending:   { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock },
    failed:    { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
    refunded:  { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: FileText },
    cancelled: { color: 'bg-gray-100 text-gray-700 border-gray-200', icon: XCircle },
  };

  const sc = statusConfig[payment.payment_status?.toLowerCase()] || statusConfig.pending;
  const StatusIcon = sc.icon;

  const methodLabels = {
    cash:          'Cash',
    bank_transfer: 'Bank Transfer',
    cheque:        'Cheque',
    upi:           'UPI',
    credit_card:   'Credit Card',
    debit_card:    'Debit Card',
    net_banking:   'Net Banking',
    other:         'Other',
  };

  const methodBadge = {
    cash:          'bg-yellow-100 text-yellow-700',
    bank_transfer: 'bg-blue-100 text-blue-700',
    upi:           'bg-indigo-100 text-indigo-700',
    cheque:        'bg-pink-100 text-pink-700',
    credit_card:   'bg-purple-100 text-purple-700',
    debit_card:    'bg-orange-100 text-orange-700',
    net_banking:   'bg-cyan-100 text-cyan-700',
    other:         'bg-gray-100 text-gray-700',
  }[payment.payment_method] || 'bg-gray-100 text-gray-700';

  const InfoRow = ({ icon: Icon, label, value, children }) => (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
      <div className="w-8 h-8 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-gray-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 mb-0.5">{label}</p>
        {children || <p className="text-sm font-semibold text-secondary break-words">{value || 'N/A'}</p>}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-secondary">Payment Details</h2>
            <p className="text-xs text-gray-400">{payment.payment_id}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">

          {/* Amount Hero */}
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
            <p className="text-xs text-orange-600 mb-1">Payment Amount</p>
            <p className="text-3xl font-bold text-orange-600">
              ₹{parseFloat(payment.amount || 0).toLocaleString('en-IN')}
            </p>
            <div className="mt-2">
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${sc.color}`}>
                <StatusIcon className="w-3 h-3" />
                {payment.payment_status?.charAt(0).toUpperCase() + payment.payment_status?.slice(1)}
              </span>
            </div>
          </div>

          {/* Subscription Info */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Subscription</p>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-orange-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-secondary">Subscription #{payment.subscription_id}</p>
                <p className="text-xs text-gray-400">Company ID: {payment.company_id}</p>
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Payment Info</p>
            <div className="bg-white border border-gray-200 rounded-xl px-2">
              <InfoRow icon={Calendar} label="Payment Date" value={formatDate(payment.payment_date)} />
              <InfoRow icon={CreditCard} label="Payment Method">
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${methodBadge}`}>
                  {methodLabels[payment.payment_method] || payment.payment_method || 'N/A'}
                </span>
              </InfoRow>
              {payment.transaction_id && (
                <InfoRow icon={Hash} label="Transaction ID" value={payment.transaction_id} />
              )}
              {payment.transaction_reference && (
                <InfoRow icon={Hash} label="Transaction Reference" value={payment.transaction_reference} />
              )}
              {payment.bank_name && (
                <InfoRow icon={Building2} label="Bank Name" value={payment.bank_name} />
              )}
              {payment.cheque_number && (
                <InfoRow icon={Hash} label="Cheque Number" value={payment.cheque_number} />
              )}
              {payment.upi_id && (
                <InfoRow icon={Hash} label="UPI ID" value={payment.upi_id} />
              )}
              {payment.notes && (
                <InfoRow icon={MessageSquare} label="Notes" value={payment.notes} />
              )}
              {payment.remarks && (
                <InfoRow icon={MessageSquare} label="Remarks" value={payment.remarks} />
              )}
            </div>
          </div>

          {/* Receipt */}
          {payment.has_receipt && payment.receipt_url && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Receipt</p>
              <a
                href={payment.receipt_url}
                target="_blank"
                rel="noreferrer"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-blue-300 rounded-xl text-blue-600 hover:bg-blue-50 transition-colors text-sm font-medium"
              >
                <FileText className="w-4 h-4" />
                View Receipt
              </a>
            </div>
          )}

          {/* Verification */}
          {payment.verified_by && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3">
              <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-2">Verified</p>
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-green-600" />
                <span className="font-semibold text-secondary">{payment.verified_by}</span>
                {payment.verified_at && (
                  <span className="text-xs text-gray-400">on {formatDateTime(payment.verified_at)}</span>
                )}
              </div>
            </div>
          )}

          {/* Audit */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Audit</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-400">{t('common.createdBy')}</p>
                <p className="text-xs font-semibold text-secondary">{payment.created_by || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Created At</p>
                <p className="text-xs font-semibold text-secondary">{formatDateTime(payment.created_at)}</p>
              </div>
              {payment.updated_at && payment.updated_at !== payment.created_at && (
                <div>
                  <p className="text-xs text-gray-400">Last Updated</p>
                  <p className="text-xs font-semibold text-secondary">{formatDateTime(payment.updated_at)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Close */}
          <Button variant="secondary" onClick={onClose} fullWidth>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ViewPaymentModal;